import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Button, Spinner, Alert, Table,
    Form, Badge, ProgressBar, OverlayTrigger, Tooltip
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { formatCurrency, formatNumber } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";


export default function ChiffreAffaires() {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [annees, setAnnees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [periode, setPeriode] = useState('mois');
    const [annee, setAnnee] = useState(new Date().getFullYear());
    const [viewMode, setViewMode] = useState('table');

    useEffect(() => {
        fetchAnnees();
        fetchData();
    }, [periode, annee]);

    const fetchAnnees = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/stats.php?action=annees`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setAnnees(result.data || []);
            }
        } catch (err) {
            console.error("Erreur chargement années", err);
        }
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/stats.php?action=ca&periode=${periode}&annee=${annee}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setData(result.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const totalCA = data.reduce((sum, item) => {
        const valeur = parseFloat(item.ca) || 0;
        return sum + valeur;
    }, 0);
    const totalFactures = data.reduce((sum, item) => sum + (item.nb_factures || 0), 0);
    const totalClients = data.reduce((sum, item) => sum + (item.nb_clients || 0), 0);
    const panierMoyen = totalFactures > 0 ? totalCA / totalFactures : 0;

    const getPeriodeLabel = (item) => {
        if (periode === 'mois') {
            return new Date(annee, item.mois - 1, 1).toLocaleString('fr-FR', { month: 'long' });
        } else if (periode === 'trimestre') {
            return `T${item.trimestre} ${item.annee}`;
        } else {
            return `${item.annee}`;
        }
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Analyse du chiffre d'affaires...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchData}>
                                    <i className="ri-refresh-line me-1"></i>Réessayer
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-bar-chart-2-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Chiffre d'affaires</h4>
                                    <p className="text-muted small mb-0">
                                        Analyse détaillée par période • {annee}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant={viewMode === 'table' ? 'primary' : 'outline-primary'}
                                size="sm"
                                onClick={() => setViewMode('table')}
                                className="rounded-pill px-3"
                                style={viewMode === 'table' ? { background: '#21ACCD', border: 'none' } : {}}
                            >
                                <i className="ri-table-line me-1"></i>Tableau
                            </Button>
                            <Button 
                                variant={viewMode === 'graph' ? 'primary' : 'outline-primary'}
                                size="sm"
                                onClick={() => setViewMode('graph')}
                                className="rounded-pill px-3"
                                style={viewMode === 'graph' ? { background: '#21ACCD', border: 'none' } : {}}
                            >
                                <i className="ri-bar-chart-2-line me-1"></i>Graphique
                            </Button>
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate('/stats/dashboard')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour
                            </Button>
                        </div>
                    </div>

                    {/* Filtres modernes */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-end g-3">
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-muted small text-uppercase">
                                            <i className="ri-calendar-line me-2"></i>
                                            Période
                                        </Form.Label>
                                        <Form.Select 
                                            value={periode} 
                                            onChange={(e) => setPeriode(e.target.value)}
                                            className="rounded-pill"
                                        >
                                            <option value="mois"> Mensuel</option>
                                            <option value="trimestre"> Trimestriel</option>
                                            <option value="annee"> Annuel</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-muted small text-uppercase">
                                            <i className="ri-calendar-2-line me-2"></i>
                                            Année
                                        </Form.Label>
                                        <Form.Select 
                                            value={annee} 
                                            onChange={(e) => setAnnee(e.target.value)}
                                            className="rounded-pill"
                                        >
                                            {annees.map(a => (
                                                <option key={a} value={a}>{a}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Button 
                                        variant="primary" 
                                        onClick={fetchData}
                                        className="w-100 rounded-pill py-2"
                                        style={{ background: '#21ACCD', border: 'none' }}
                                    >
                                        <i className="ri-refresh-line me-2"></i>
                                        Actualiser
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* KPI Cards modernes */}
                    <Row className="g-3 mb-4">
                        <Col sm={6} xl={3}>
                            <div className="kpi-card kpi-primary">
                                <div className="kpi-icon">
                                    <i className="ri-money-euro-circle-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">CA Total</span>
                                    <h3 className="kpi-value">{formatCurrency(totalCA)}</h3>
                                </div>
                            </div>
                        </Col>
                        <Col sm={6} xl={3}>
                            <div className="kpi-card kpi-info">
                                <div className="kpi-icon">
                                    <i className="ri-file-list-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Factures</span>
                                    <h3 className="kpi-value">{formatNumber(totalFactures)}</h3>
                                </div>
                            </div>
                        </Col>
                        <Col sm={6} xl={3}>
                            <div className="kpi-card kpi-warning">
                                <div className="kpi-icon">
                                    <i className="ri-group-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Clients</span>
                                    <h3 className="kpi-value">{formatNumber(totalClients)}</h3>
                                </div>
                            </div>
                        </Col>
                        <Col sm={6} xl={3}>
                            <div className="kpi-card kpi-success">
                                <div className="kpi-icon">
                                    <i className="ri-shopping-basket-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Panier moyen</span>
                                    <h3 className="kpi-value" style={{ fontSize: '1rem' }}>{formatCurrency(panierMoyen)}</h3>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Vue Tableau */}
                    {viewMode === 'table' && (
                        <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                            <Card.Header className="bg-transparent border-0 pt-4 pb-3">
                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="section-icon-small">
                                            <i className="ri-file-list-line text-primary"></i>
                                        </div>
                                        <h6 className="mb-0 fw-semibold">
                                            Détail par {periode === 'mois' ? 'mois' : periode === 'trimestre' ? 'trimestre' : 'année'}
                                        </h6>
                                    </div>
                                    <Badge className="px-3 py-2 rounded-pill" style={{ background: '#21ACCD' }}>
                                        {data.length} période(s)
                                    </Badge>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-0">
                                {loading ? (
                                    <div className="text-center py-5">
                                        <Spinner animation="border" variant="primary" style={{ color: '#21ACCD' }} />
                                    </div>
                                ) : data.length === 0 ? (
                                    <div className="text-center py-5">
                                        <i className="ri-inbox-line fs-48 text-muted mb-3 d-block"></i>
                                        <h5>Aucune donnée disponible</h5>
                                        <p className="text-muted">Aucune facture trouvée pour cette période</p>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <Table hover className="align-middle mb-0 custom-table">
                                            <thead className="bg-light">
                                                <tr>
                                                    <th className="ps-4">Période</th>
                                                    <th className="text-end">Chiffre d'affaires</th>
                                                    <th className="text-end">Factures</th>
                                                    <th className="text-end">Clients</th>
                                                    <th className="text-end">Panier moyen</th>
                                                    <th className="text-center pe-4">Tendance</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data.map((item, index) => {
                                                    const caPrecedent = index > 0 ? data[index - 1].ca : item.ca;
                                                    const evolution = caPrecedent > 0 ? ((item.ca - caPrecedent) / caPrecedent * 100).toFixed(1) : 0;
                                                    const libelle = getPeriodeLabel(item);
                                                    
                                                    return (
                                                        <tr key={index} className="ca-row">
                                                            <td className="ps-4">
                                                                <span className="fw-semibold text-capitalize">{libelle}</span>
                                                            </td>
                                                            <td className="text-end fw-bold text-primary">
                                                                {formatCurrency(item.ca)}
                                                            </td>
                                                            <td className="text-end">
                                                                <Badge className="rounded-pill px-3" style={{ background: '#e2f3f5', color: '#fff' }}>
                                                                    {item.nb_factures}
                                                                </Badge>
                                                            </td>
                                                            <td className="text-end">
                                                                {item.nb_clients || <span className="text-muted">-</span>}
                                                            </td>
                                                            <td className="text-end fw-semibold text-success">
                                                                {formatCurrency(item.ca / (item.nb_factures || 1))}
                                                            </td>
                                                            <td className="text-center pe-4">
                                                                {index > 0 && (
                                                                    <OverlayTrigger
                                                                        placement="top"
                                                                        overlay={<Tooltip>Évolution: {evolution}%</Tooltip>}
                                                                    >
                                                                        <span className={`status-badge ${evolution >= 0 ? 'text-success' : 'text-danger'}`} style={{ background: evolution >= 0 ? '#10b98115' : '#ef444415' }}>
                                                                            <i className={`ri-arrow-${evolution >= 0 ? 'up' : 'down'}-line me-1 fs-10`}></i>
                                                                            {Math.abs(evolution)}%
                                                                        </span>
                                                                    </OverlayTrigger>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    {/* Vue Graphique */}
                    {viewMode === 'graph' && (
                        <Card className="border-0 shadow-sm rounded-3">
                            <Card.Header className="bg-transparent border-0 pt-4 pb-3">
                                <div className="d-flex align-items-center gap-2">
                                    <div className="section-icon-small">
                                        <i className="ri-bar-chart-2-line text-primary"></i>
                                    </div>
                                    <h6 className="mb-0 fw-semibold">Évolution du chiffre d'affaires</h6>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-4">
                                {data.length === 0 ? (
                                    <div className="text-center py-5">
                                        <i className="ri-bar-chart-line fs-48 text-muted mb-3 d-block"></i>
                                        <h5>Aucune donnée à afficher</h5>
                                    </div>
                                ) : (
                                    <div className="graph-container">
                                        <div className="graph-bars">
                                            {data.map((item, index) => {
                                                const maxCa = Math.max(...data.map(d => d.ca));
                                                const height = maxCa > 0 ? (item.ca / maxCa) * 200 : 0;
                                                const libelle = getPeriodeLabel(item);
                                                
                                                return (
                                                    <div key={index} className="graph-bar-item">
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={<Tooltip>{formatCurrency(item.ca)}</Tooltip>}
                                                        >
                                                            <div 
                                                                className="graph-bar"
                                                                style={{ height: `${height}px` }}
                                                            ></div>
                                                        </OverlayTrigger>
                                                        <small className="graph-label text-capitalize">{libelle}</small>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <div className="graph-legend mt-4 pt-3 text-center text-muted small">
                                            <i className="ri-information-line me-1"></i>
                                            Survolez les barres pour voir les montants
                                        </div>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    {/* Pied de tableau avec totaux */}
                    {data.length > 0 && viewMode === 'table' && (
                        <Card className="border-0 shadow-sm rounded-3 mt-3" style={{ background: '#f8fafc' }}>
                            <Card.Body className="p-3">
                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                    <span className="text-muted small">Total général</span>
                                    <div className="d-flex gap-4">
                                        <span className="fw-bold text-primary">{formatCurrency(totalCA)}</span>
                                        <span className="fw-bold" style={{ color: '#21ACCD' }}>{formatNumber(totalFactures)}</span>
                                        <span className="fw-bold text-success">{formatCurrency(panierMoyen)}</span>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    )}
                </div>
                <Footer />
            </div>

            <style>{`
                /* KPI Cards */
                .kpi-card {
                    background: white;
                    border-radius: 0px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                    height: 100%;
                    border-left: 2px solid;
                }
                .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: #21ACCD; }
                .kpi-info { border-left-color: #0ea5e9; }
                .kpi-warning { border-left-color: #f59e0b; }
                .kpi-success { border-left-color: #10b981; }
                
                .kpi-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .kpi-primary .kpi-icon { background: #e2f3f5; color: #21ACCD; }
                .kpi-info .kpi-icon { background: #e2f3f5; color: #0ea5e9; }
                .kpi-warning .kpi-icon { background: #fffbeb; color: #f59e0b; }
                .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
                
                .kpi-content {
                    flex: 1;
                }
                .kpi-label {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    color: #6c757d;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value {
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0.25rem 0;
                    color: #1a3c5e;
                }
                
                /* Status Badge */
                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.3rem 0.8rem;
                    border-radius: 30px;
                    font-size: 0.7rem;
                    font-weight: 500;
                }
                
                /* Section Icon Small */
                .section-icon-small {
                    width: 32px;
                    height: 32px;
                    background: linear-gradient(135deg, #21ACCD20, #1a8ba820);
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                /* Custom Table */
                .custom-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem 0.75rem;
                    border-bottom: 2px solid #e2e8f0;
                }
                
                .custom-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .ca-row {
                    transition: all 0.2s ease;
                }
                
                .ca-row:hover {
                    background: #f8fafc;
                }
                
                /* Graphique */
                .graph-container {
                    padding: 1rem;
                }
                .graph-bars {
                    display: flex;
                    align-items: flex-end;
                    justify-content: space-around;
                    min-height: 250px;
                }
                .graph-bar-item {
                    text-align: center;
                    width: 60px;
                }
                .graph-bar {
                    background: #21ACCD;
                    width: 40px;
                    margin: 0 auto 0.5rem;
                    border-radius: 8px 8px 0 0;
                    transition: all 0.3s ease;
                    cursor: pointer;
                }
                .graph-bar:hover {
                    background: #1a8ba8;
                    transform: scaleX(1.05);
                }
                .graph-label {
                    font-size: 0.7rem;
                    color: #64748b;
                }
                .graph-legend {
                    font-size: 0.7rem;
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .fs-10 {
                    font-size: 0.625rem;
                }
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-24 {
                    font-size: 1.5rem;
                }
                .fs-48 {
                    font-size: 3rem;
                }
            `}</style>
        </>
    );
}